<template>
	<AuthShell>
		<v-container class="main-container" fluid>
			<v-card class="login-card">
				<v-card-title class="modal-header login-title">{{ $t('reset_password') }}</v-card-title>
				<v-divider></v-divider>
				<v-card-text class="login-body">
					<form class="modal-body login-form" @submit.prevent="submit">
						<p v-if="!token" class="system-error" role="alert">{{ $t('reset_password_missing_token') }}</p>

						<template v-else-if="!done">
							<p class="hint">{{ $t('reset_password_hint') }}</p>

							<div class="field-block">
								<v-text-field
									ref="newPasswordField"
									class="input"
									:placeholder="$t('new_password')"
									v-model="newPassword"
									:type="showPassword ? 'text' : 'password'"
									:append-icon="showPassword ? eyeOffIcon : eyeIcon"
									outlined
									hide-details="auto"
									autocomplete="new-password"
									:error="!!newPasswordError"
									:error-messages="newPasswordError"
									:disabled="isLoading"
									@click:append="showPassword = !showPassword"
									@input="clearErrors"
								></v-text-field>
							</div>

							<div class="field-block">
								<v-text-field
									ref="confirmPasswordField"
									class="input"
									:placeholder="$t('confirm_new_password')"
									v-model="confirmPassword"
									:type="showConfirmPassword ? 'text' : 'password'"
									:append-icon="showConfirmPassword ? eyeOffIcon : eyeIcon"
									outlined
									hide-details="auto"
									autocomplete="new-password"
									:error="!!confirmPasswordError"
									:error-messages="confirmPasswordError"
									:disabled="isLoading"
									@click:append="showConfirmPassword = !showConfirmPassword"
									@input="clearErrors"
								></v-text-field>
							</div>

							<p class="sub-hint">{{ $t('password_min_length') }}</p>
							<p v-if="systemError" class="system-error" role="alert">{{ systemError }}</p>
						</template>

						<p v-else class="success-banner" role="status">{{ $t('password_reset_successfully') }}</p>
					</form>
				</v-card-text>
				<v-card-actions class="buttons-container buttons-container--split">
					<Button
						v-if="token && !done"
						skin="primary"
						:disabled="isLoading"
						@click="submit"
					>
						{{ isLoading ? $t('loading') : $t('reset_password') }}
					</Button>
					<Button v-else-if="done" skin="primary" @click="$router.push('/login')">
						{{ $t('back_to_login') }}
					</Button>
					<router-link v-if="!done" class="back-link" to="/login">{{ $t('back_to_login') }}</router-link>
				</v-card-actions>
			</v-card>
		</v-container>
	</AuthShell>
</template>

<script>
import { mdiEye, mdiEyeOff } from '@mdi/js'
import Button from '@/modules/common/components/Button'
import AuthShell from '@/modules/auth/components/AuthShell'
import authService from '@/modules/auth/services/authService'
import { PASSWORD_MIN_LENGTH, focusField } from '@/modules/auth/passwordRules'

export default {
	name: 'ResetPassword',
	components: { Button, AuthShell },
	data() {
		return {
			newPassword: '',
			confirmPassword: '',
			isLoading: false,
			done: false,
			showPassword: false,
			showConfirmPassword: false,
			newPasswordError: '',
			confirmPasswordError: '',
			systemError: '',
			eyeIcon: mdiEye,
			eyeOffIcon: mdiEyeOff
		}
	},
	computed: {
		token() {
			const t = this.$route.query.token
			return typeof t === 'string' && t.trim() ? t.trim() : ''
		}
	},
	mounted() {
		if (!this.token) return
		this.$nextTick(() => focusField(this.$refs.newPasswordField))
	},
	methods: {
		clearErrors() {
			this.newPasswordError = ''
			this.confirmPasswordError = ''
			this.systemError = ''
		},
		validate() {
			this.clearErrors()
			if (this.newPassword.length < PASSWORD_MIN_LENGTH) {
				this.newPasswordError = this.$t('password_min_length')
				return false
			}
			if (this.newPassword !== this.confirmPassword) {
				this.confirmPasswordError = this.$t('passwords_do_not_match')
				return false
			}
			return true
		},
		async submit() {
			if (this.isLoading || this.done || !this.token) return
			if (!this.validate()) {
				this.$nextTick(() => {
					focusField(this.newPasswordError
						? this.$refs.newPasswordField
						: this.$refs.confirmPasswordField)
				})
				return
			}

			this.isLoading = true
			try {
				await authService.resetPassword(this.token, this.newPassword)
				this.done = true
				this.newPassword = ''
				this.confirmPassword = ''
			} catch (err) {
				const codeToMessage = {
					INVALID_OR_EXPIRED_TOKEN: this.$t('reset_password_invalid_token'),
					PASSWORD_TOO_SHORT: this.$t('password_min_length'),
					PASSWORD_SAME_AS_CURRENT: this.$t('new_password_same_as_current'),
					TOO_MANY_RESET_ATTEMPTS: this.$t('reset_password_too_many')
				}
				const code = err && err.validationCode
				if (code === 'PASSWORD_TOO_SHORT' || code === 'PASSWORD_SAME_AS_CURRENT') {
					this.newPasswordError = codeToMessage[code]
				} else {
					this.systemError = (code && codeToMessage[code])
						|| (err && err.message)
						|| this.$t('reset_password_failed')
				}
			} finally {
				this.isLoading = false
			}
		}
	}
}
</script>

<style lang="scss" scoped>
@use '../styles/authCard';
</style>
